import React, { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const DropzoneContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: '#111',
  border: '2px dashed #333',
  borderRadius: '24px',
  padding: theme.spacing(6),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: '#FFF',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  '&:hover': {
    borderColor: '#10B981',
    backgroundColor: 'rgba(16, 185, 129, 0.05)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
  },
}));

interface MediaUploaderProps {
  onFilesAccepted: (files: File[]) => void;
}

const MediaUploader: FC<MediaUploaderProps> = ({ onFilesAccepted }) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    onFilesAccepted(acceptedFiles);
  }, [onFilesAccepted]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.ogg'],
      'audio/*': ['.mp3', '.wav', '.aac'],
    }
  });

  return (
    <DropzoneContainer {...getRootProps()} elevation={0}>
      <input {...getInputProps()} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box sx={{ 
          p: 2, 
          borderRadius: '50%', 
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981',
          mb: 1
        }}>
          <CloudUploadIcon sx={{ fontSize: 40 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {isDragActive ? 'Drop your files here' : 'Drop your files here, or browse'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Supports high-resolution images, videos, and multi-channel audio
          </Typography>
        </Box>
      </Box>
    </DropzoneContainer>
  );
};

export default MediaUploader;
